import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import CustomerNav from './CustomerNav';
import { FileText, Clock, CheckCircle, XCircle, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDisplayAmount } from '../utils/offerLineDisplay';

const CustomerPortal = () => {
  const { t } = useTranslation();
  const { chatbot, getAuthHeaders, logout } = useCustomerAuth();
  const [stats, setStats] = useState(null);
  const [recentOffers, setRecentOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchRecentOffers();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/customer/offers/stats', {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecentOffers = async () => {
    try {
      const response = await fetch('/api/customer/offers?limit=5', {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setRecentOffers(data.offers || []);
      }
    } catch (error) {
      console.error('Error fetching recent offers:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {t('customer.status.pending')}
        </span>
      ),
      accepted: (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          {t('customer.status.accepted')}
        </span>
      ),
      rejected: (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          {t('customer.status.rejected')}
        </span>
      )
    };
    return badges[status] || badges.pending;
  };

  const formatAmount = (amount, offer) => {
    const formatted = formatDisplayAmount(amount, offer);
    if (formatted === null) {
      return offer?.displayMode === 'hidden' ? t('offer.pricesHidden') : 'N/A';
    }
    return formatted;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CustomerNav />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600">{t('customer.dashboard.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CustomerNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('customer.dashboard.welcome', { name: chatbot?.companyName || chatbot?.name || 'Customer' })}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('customer.dashboard.subtitle')}
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('customer.dashboard.totalOffers')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalOffers || 0}</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <FileText className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('customer.dashboard.pending')}</p>
                  <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pendingOffers || 0}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('customer.dashboard.accepted')}</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{stats.acceptedOffers || 0}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('customer.dashboard.acceptanceRate')}</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-1">{stats.acceptanceRate || 0}%</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Offers */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('customer.dashboard.recentOffers')}</h2>
            
            <button
              onClick={() => navigate('/customer/offers')}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="divide-y divide-gray-200">
            {recentOffers.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">{t('customer.dashboard.noOffersYet')}</p>
                <p className="text-sm text-gray-500 mt-1">{t('customer.dashboard.offersWillAppear')}</p>
              </div>
            ) : (
              recentOffers.map((offer) => (
                <div
                  key={offer.id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition"
                  onClick={() => navigate(`/customer/offers/${offer.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusBadge(offer.status)}
                        <span className="text-sm text-gray-500">
                          {new Date(offer.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{offer.offerDetails}</p>
                      {offer.customerInfo?.name && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('customer.dashboard.customer')}: {offer.customerInfo.name}
                        </p>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      <p className="text-lg font-semibold text-gray-900">
                        {formatAmount(offer.totalAmount, offer)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPortal;
